/**
 * BCP-47 locale-tag resolution for Intl/Luxon locale-aware formatting.
 *
 * Mirrors the calendar widget's #1530 convention (CalendarGrid.tsx
 * `toBcp47`/`isEnglish`, used there for month/weekday labels) so a future
 * consolidation can drop these exports in without a behavior change — this
 * PR does not refactor the calendar to use them (#1429 scope: booking only).
 *
 * `locale` is the operator's configured/detected value in `xx_YY` shape
 * (`useLocaleDetection`'s `detectedLocale`).
 *
 * Checkout and confirmation (#1735/#1737) resolve money, dates and the
 * property title through the same locale, with the helpers at the bottom.
 */

/** BCP-47 tag for a `xx_YY` locale string, or `null` if it isn't well-formed. */
export function toBcp47(locale: string | undefined): string | null {
  if (!locale) return null
  const tag = locale.replace('_', '-')
  return /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(tag) ? tag : null
}

/** True for an English locale (`en`, `en_US`, `en_GB`, …) or an unset one. */
export function isEnglish(locale: string | undefined): boolean {
  return !locale || /^en([_-]|$)/i.test(locale)
}

/**
 * The BCP-47 tag to pass to `Intl`/Luxon, or `null` when the caller should
 * keep its pre-locale-aware default (English, unset, or an unusable tag) —
 * callers treat `null` as "use today's hard-coded behavior" so the en_US /
 * unset path stays byte-identical to before locale-aware formatting existed.
 */
export function resolveLocaleTag(locale: string | undefined): string | null {
  return isEnglish(locale) ? null : toBcp47(locale)
}

/**
 * `resolveLocaleTag`, plus a check that Intl actually accepts the tag — null
 * when it does not. The regex in `toBcp47` lets through tags no engine knows:
 * a bare 3-letter page language (`<html lang="oci">`, Occitan) reaches us as
 * `oci_OCI` and becomes `oci-OCI`, which `Intl.NumberFormat` rejects. Leaves
 * that cannot catch that themselves (common/utils formatPrice degrades to a
 * ROUNDED "EUR 530") must never see such a tag, so checkout and confirmation
 * resolve it once, here, and every leaf gets either a working tag or null —
 * null being the English path they print today.
 */
export function resolveIntlLocaleTag(locale: string | undefined): string | null {
  const tag = resolveLocaleTag(locale)
  if (!tag) return null
  try {
    new Intl.NumberFormat(tag)
    return tag
  } catch {
    return null
  }
}

/**
 * Money for the checkout and confirmation surfaces (#1737), in the widget's
 * locale. `localeTag` is `resolveIntlLocaleTag(detectedLocale)`.
 *
 * The English path is exactly what those widgets printed before (#1737 only
 * adds the non-English branch): `en-US`, the currency's own fraction digits
 * (EUR 1765 stays "€1,765.00" — booking's 0–2 digit rule is NOT copied, it
 * would change English output), and the `$` last resort for a currency code
 * Intl rejects. A tag Intl rejects falls back to that English path too.
 */
export function formatLocaleCurrency(amount: number, currency: string, localeTag: string | null): string {
  const format = (locale: string) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)
  if (localeTag) {
    try {
      return format(localeTag)
    } catch {
      // An unusable tag (or currency) gets the English output below.
    }
  }
  try {
    return format('en-US')
  } catch {
    return `$${amount.toFixed(2)}`
  }
}

/** `nl-BE`, `NL_be`, ` nl_BE ` → `nl_be`; null for a blank or non-string value. */
function localeKey(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const key = value.trim().replace(/-/g, '_').toLowerCase()
  return key === '' ? null : key
}

/**
 * How well a locale key fits the visitor's locale: 2 = the same locale (case
 * and `-`/`_` ignored, so Central's `nl_nl` matches `nl_NL`), 1 = the same
 * language only (`nl_NL` or a bare `nl` for an `nl_BE` visitor), 0 = no fit.
 * Never crosses languages: `fr_FR` is no fit for `nl_BE`.
 */
export function localeMatchScore(key: unknown, locale: string | undefined): 0 | 1 | 2 {
  const k = localeKey(key)
  const l = localeKey(locale)
  if (!k || !l) return 0
  if (k === l) return 2
  return k.split('_')[0] === l.split('_')[0] ? 1 : 0
}

/**
 * The property title in the visitor's language (#1735).
 *
 * `localizedTitles` is Central's feed-scoped `localized_titles` map — one title
 * per description locale, built from the feed's title source and editable per
 * feed in Central's admin — e.g. `{ en_us: …, nl_nl: …, fr_fr: … }`. Its keys
 * are lowercase, so the lookup ignores case, and an `nl_BE` visitor gets
 * `nl_nl` through the same-language rule (an exact `nl_be` entry wins).
 *
 * English and unset locales keep `title` untouched: that is the feed-scoped
 * title Central already resolved (it can carry a per-feed rename the
 * localized map does not), so an English-locale visitor sees exactly what
 * they saw before. (On the auto-detect path the locale is the page's
 * `<html lang>`, else the browser's — a Dutch browser on a page that
 * declares no language gets the Dutch title, as booking already formats its
 * prices for that browser since #1429.)
 * No usable entry for the visitor's language — or a junk value, like PHP's
 * `[]` for an empty map — also keeps `title`.
 */
export function resolveLocalizedTitle(title: string, localizedTitles: unknown, locale: string | undefined): string {
  if (isEnglish(locale)) return title
  if (!localizedTitles || typeof localizedTitles !== 'object' || Array.isArray(localizedTitles)) return title
  let best: string | null = null
  let bestScore = 0
  for (const [key, value] of Object.entries(localizedTitles as Record<string, unknown>)) {
    if (typeof value !== 'string' || value.trim() === '') continue
    const score = localeMatchScore(key, locale)
    if (score > bestScore) {
      best = value
      bestScore = score
    }
  }
  return best ?? title
}
